import { createElement, Fragment, useSyncExternalStore, type ReactNode } from 'react';
import en from '@/locales/en.json';
import de from '@/locales/de.json';
import es from '@/locales/es.json';
import fr from '@/locales/fr.json';
import it from '@/locales/it.json';
import nl from '@/locales/nl.json';

/**
 * Tiny dependency-free i18n. `t()` is a plain function usable anywhere during
 * render; switching language re-keys the whole app (<I18nRoot>), so every
 * component re-renders with the new dictionary — no per-component hook needed.
 * English is the source language and the fallback for any missing key.
 */
export const LANGUAGES = {
  en: 'English',
  de: 'Deutsch',
  es: 'Español',
  fr: 'Français',
  it: 'Italiano',
  nl: 'Nederlands',
} as const;
export type Lang = keyof typeof LANGUAGES;

type Dict = Record<string, string>;
const DICTS: Record<Lang, Dict> = { en, de, es, fr, it, nl };
const KEY = 'ecomstat-dam-lang';

function isLang(v: unknown): v is Lang {
  return typeof v === 'string' && v in LANGUAGES;
}

function detect(): Lang {
  try {
    const stored = localStorage.getItem(KEY);
    if (isLang(stored)) return stored;
  } catch {
    /* storage blocked — fall through to the browser */
  }
  for (const l of navigator.languages ?? [navigator.language]) {
    const base = l?.slice(0, 2).toLowerCase();
    if (isLang(base)) return base;
  }
  return 'en';
}

let current: Lang = detect();
document.documentElement.lang = current;
const listeners = new Set<() => void>();

export function getLang(): Lang {
  return current;
}

export function setLang(lang: Lang) {
  if (lang === current) return;
  current = lang;
  document.documentElement.lang = lang;
  try {
    localStorage.setItem(KEY, lang);
  } catch {
    /* ignore */
  }
  listeners.forEach((l) => l());
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** Current language, re-rendering the caller when it changes. */
export function useLang(): Lang {
  return useSyncExternalStore(subscribe, getLang);
}

/** Translate `key`, filling `{name}` placeholders from `vars`. */
export function t(key: string, vars?: Record<string, string | number>): string {
  const s = DICTS[current][key] ?? DICTS.en[key] ?? key;
  if (!vars) return s;
  return s.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));
}

/** Re-mounts the app on a language switch so every t() call re-evaluates. */
export function I18nRoot({ children }: { children: ReactNode }) {
  const lang = useLang();
  return createElement(Fragment, { key: lang }, children);
}
